import React from 'react';
import { colors } from '../../styles/tokens';
import { fmtMoney } from '../../utils/format';
import { acumular, diaCorto } from '../../utils/meta';
import { useEsMovil } from '../../hooks/useEsMovil';
import type { MetaSerieDia } from '../../types/indicadores.types';

export interface GraficoMetaProps {
  serie: MetaSerieDia[];
  /** Día de hoy en hora de México, para el marcador vertical. */
  hoy: string;
}

const W = 980;
const H = 260;
const PAD = { top: 18, right: 16, bottom: 30, left: 76 };

/**
 * Geometría del viewBox en móvil.
 *
 * POR QUÉ AQUÍ SÍ CAMBIA EL viewBox (y en `GraficoHora` no): esta gráfica
 * calcula su propia geometría a partir de estas constantes —no hay una utilería
 * compartida ni pruebas atadas a las coordenadas—, así que puede tener una caja
 * proporcionada al teléfono en vez de escalar una de escritorio.
 *
 * Y hacía falta: 980 × 260 sobre los ~340 px reales de un teléfono deja una
 * franja de **90 px de alto** con letra de 3,8 px. Peor todavía, el SVG pedía
 * `height={260}` fijo, así que esos 90 px de dibujo vivían centrados dentro de
 * una caja de 260: **170 px de vacío** ocupando pantalla. Con 400 × 250 la
 * gráfica se pinta a ~212 px de alto, sin hueco, y una fuente de 13 unidades
 * son 11 px reales.
 */
const W_MOVIL = 400;
const H_MOVIL = 250;
const PAD_MOVIL = { top: 16, right: 24, bottom: 26, left: 52 };

/**
 * Importe abreviado para el eje Y en móvil: `$40,561,132` -> `$41M`,
 * `$247,300` -> `$247K`.
 *
 * El margen izquierdo de la caja móvil son 52 unidades; un importe completo a
 * tamaño legible ocupa casi 200 y se cortaría contra el filo del viewBox. La
 * cifra exacta no se pierde: la leyenda de arriba sigue mostrando la meta y la
 * venta con `fmtMoney`, y el eje sólo da la escala.
 */
function fmtMoneyCorto(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    return `$${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  }
  if (abs >= 1_000) {
    return `$${Math.round(n / 1_000)}K`;
  }
  return fmtMoney(n);
}

/**
 * Curva acumulada: meta contra venta, día a día.
 *
 * Se acumula porque la pregunta del mes es de saldo, no de caudal: importa si
 * la venta corrida va por encima o por debajo de la meta corrida, no cuánto se
 * vendió un martes suelto.
 *
 * ⚠️ La línea de venta se CORTA en el primer día sin dato en vez de seguir
 * plana. Una meseta en el tramo futuro se lee como «dejaron de vender», que es
 * lo contrario de «todavía no ha pasado».
 */
const GraficoMeta: React.FC<GraficoMetaProps> = ({ serie, hoy }) => {
  const esMovil = useEsMovil();
  const puntos = acumular(serie, 'monto');
  if (puntos.length === 0) {
    return null;
  }

  // Caja del dibujo: la de escritorio queda intacta cuando `esMovil` es false.
  const w = esMovil ? W_MOVIL : W;
  const h = esMovil ? H_MOVIL : H;
  const pad = esMovil ? PAD_MOVIL : PAD;
  const fuenteEje = esMovil ? 13 : 11;

  const maxY = Math.max(
    1,
    ...puntos.map((p) => Math.max(p.meta, p.venta ?? 0)),
  );
  const innerW = w - pad.left - pad.right;
  const innerH = h - pad.top - pad.bottom;
  const x = (i: number) =>
    pad.left + (puntos.length === 1 ? innerW / 2 : (i / (puntos.length - 1)) * innerW);
  const y = (v: number) => pad.top + innerH - (v / maxY) * innerH;

  const linea = (sel: (p: (typeof puntos)[number]) => number | null) =>
    puntos
      .map((p, i) => ({ v: sel(p), i }))
      .filter((p): p is { v: number; i: number } => p.v !== null)
      .map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`)
      .join(' ');

  const areaVenta = (() => {
    const vis = puntos
      .map((p, i) => ({ v: p.venta, i }))
      .filter((p): p is { v: number; i: number } => p.v !== null);
    if (vis.length === 0) return '';
    const arriba = vis
      .map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.v).toFixed(1)}`)
      .join(' ');
    const ultimo = vis[vis.length - 1];
    return `${arriba} L${x(ultimo.i).toFixed(1)},${y(0).toFixed(1)} L${x(vis[0].i).toFixed(1)},${y(0).toFixed(1)} Z`;
  })();

  const idxHoy = puntos.findIndex((p) => p.fecha === hoy);
  const ultimoConVenta = [...puntos].reverse().find((p) => p.venta !== null);
  const metaFinal = puntos[puntos.length - 1].meta;
  const paso = Math.max(1, Math.ceil(puntos.length / 12));

  /**
   * Marcas del eje X en móvil: **primera, hoy y última**, y nada más.
   *
   * Un mes son 30 etiquetas `DD/MM`; a tamaño legible ocupan 36 unidades de las
   * 324 del área, así que sólo caben unas ocho y encima ilegibles por
   * apelotonadas. Las tres que quedan son las que contestan la pregunta de la
   * vista: dónde empieza el periodo, dónde vamos y hasta dónde llega.
   *
   * Se descarta la marca que quedaría pegada a otra (menos de 60 unidades), y
   * en ese empate manda el cierre del periodo sobre "hoy", que ya tiene su
   * propia línea rotulada dentro del gráfico.
   */
  const marcasMovil: number[] = (() => {
    const n = puntos.length;
    const candidatas = [0];
    if (idxHoy > 0 && idxHoy < n - 1) candidatas.push(idxHoy);
    if (n > 1) candidatas.push(n - 1);

    const salida: number[] = [];
    for (const i of candidatas) {
      const previa = salida[salida.length - 1];
      if (previa === undefined || x(i) - x(previa) >= 60) {
        salida.push(i);
      } else if (i === n - 1) {
        salida[salida.length - 1] = i;
      }
    }
    return salida;
  })();

  const marcaVisible = (i: number) =>
    esMovil ? marcasMovil.includes(i) : i % paso === 0 || i === puntos.length - 1;

  // "hoy" se rotula a la derecha de su línea; si la línea está pegada al borde
  // derecho (fin de mes), el rótulo se voltea hacia adentro para no cortarse.
  const hoyAlFinal = esMovil && idxHoy >= 0 && x(idxHoy) > w - 60;

  return (
    <div
      style={{
        background: colors.card,
        border: `1px solid ${colors.border}`,
        borderRadius: 18,
        // Márgenes laterales de móvil: cada píxel que se quita al padding se lo
        // gana el ancho del dibujo, que es de donde sale la legibilidad.
        padding: esMovil ? '14px 12px' : '18px 22px',
        boxShadow: '0 1px 2px rgba(20,28,54,.05)',
      }}
    >
      <div
        style={{
          display: 'flex',
          // Título arriba y leyenda debajo: los dos importes completos y el
          // título no caben en una fila de 340 px.
          flexDirection: esMovil ? 'column' : 'row',
          alignItems: esMovil ? 'flex-start' : 'baseline',
          justifyContent: 'space-between',
          gap: esMovil ? 6 : 0,
          marginBottom: esMovil ? 8 : 10,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: colors.texto.primary }}>
          Acumulado del periodo
        </h3>
        <span
          style={{
            display: 'flex',
            gap: esMovil ? 12 : 16,
            // Con importes largos la leyenda pasa a dos renglones en vez de
            // desbordar la tarjeta.
            flexWrap: esMovil ? 'wrap' : undefined,
            // Sólo en móvil: en escritorio, permitir que el bloque encoja por
            // debajo de su contenido cambiaría el reparto actual de la fila.
            minWidth: esMovil ? 0 : undefined,
            fontSize: 12.5,
            color: colors.texto.secondary,
          }}
        >
          <span>
            <span style={{ display: 'inline-block', width: 18, height: 2, background: colors.texto.tertiary, verticalAlign: 'middle', marginRight: 6 }} />
            Meta {fmtMoney(metaFinal)}
          </span>
          <span>
            <span style={{ display: 'inline-block', width: 18, height: 3, background: colors.brand, verticalAlign: 'middle', marginRight: 6 }} />
            Venta {ultimoConVenta?.venta !== undefined && ultimoConVenta?.venta !== null ? fmtMoney(ultimoConVenta.venta) : '—'}
          </span>
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <svg
          viewBox={`0 0 ${w} ${h}`}
          width="100%"
          // En móvil la altura la manda la proporción del viewBox: con el 260
          // fijo, el dibujo (90 px) quedaba centrado dentro de una caja de 260
          // y sobraban 170 px de aire.
          height={esMovil ? undefined : H}
          style={esMovil ? { width: '100%', height: 'auto', display: 'block' } : undefined}
          role="img"
          aria-label="Acumulado de venta contra meta"
        >
          {/* En móvil, tres líneas de rejilla (0 / 50 / 100 %) en vez de cinco:
              a 208 unidades de alto y con la letra ya legible, cinco etiquetas
              se pisan entre sí. */}
          {(esMovil ? [0, 0.5, 1] : [0, 0.25, 0.5, 0.75, 1]).map((f) => (
            <g key={f}>
              <line x1={pad.left} x2={w - pad.right} y1={y(maxY * f)} y2={y(maxY * f)} stroke={colors.border} strokeWidth={1} />
              <text
                x={pad.left - 10}
                y={y(maxY * f) + (esMovil ? 5 : 4)}
                textAnchor="end"
                fontSize={fuenteEje}
                fill={colors.texto.quaternary}
              >
                {esMovil ? fmtMoneyCorto(maxY * f) : fmtMoney(maxY * f)}
              </text>
            </g>
          ))}

          {idxHoy >= 0 && (
            <>
              <rect
                x={x(idxHoy)}
                y={pad.top}
                width={Math.max(0, w - pad.right - x(idxHoy))}
                height={innerH}
                fill="#f4f6fa"
              />
              <line x1={x(idxHoy)} x2={x(idxHoy)} y1={pad.top} y2={pad.top + innerH} stroke={colors.texto.tertiary} strokeDasharray="3 3" />
              <text
                x={hoyAlFinal ? x(idxHoy) - 5 : x(idxHoy) + 5}
                y={pad.top + fuenteEje}
                textAnchor={hoyAlFinal ? 'end' : 'start'}
                fontSize={fuenteEje}
                fill={colors.texto.tertiary}
              >
                hoy
              </text>
            </>
          )}

          {areaVenta && <path d={areaVenta} fill={colors.brand} opacity={0.08} />}
          <path d={linea((p) => p.meta)} fill="none" stroke={colors.texto.tertiary} strokeWidth={2} strokeDasharray="6 4" />
          <path d={linea((p) => p.venta)} fill="none" stroke={colors.brand} strokeWidth={3} strokeLinejoin="round" />

          {puntos.map((p, i) =>
            marcaVisible(i) ? (
              <text key={p.fecha} x={x(i)} y={h - 10} textAnchor="middle" fontSize={fuenteEje} fill={colors.texto.quaternary}>
                {diaCorto(p.fecha)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </div>
  );
};

export default GraficoMeta;
